import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Trash2, ExternalLink, Star, Gift } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { getWishItems, deleteWishItem, type WishItem } from "@/services/wishlists";
import AddWishModal from "./AddWishModal";

interface RecipientList {
  id: string;
  title: string;
  type: string;
  cover_url: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientWishlists: RecipientList[];
  recipientName?: string;
}

const OrganizerRegistryModal = ({ open, onOpenChange, recipientWishlists, recipientName }: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [items, setItems] = useState<WishItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (open && recipientWishlists.length > 0 && !activeListId) {
      setActiveListId(recipientWishlists[0].id);
    }
  }, [open, recipientWishlists, activeListId]);

  const loadItems = async (listId: string) => {
    setLoading(true);
    const { data } = await getWishItems(listId);
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (activeListId && open) loadItems(activeListId);
  }, [activeListId, open]);

  const handleDelete = async (item: WishItem) => {
    if (item.added_by_user_id !== user?.id) {
      toast({ variant: "destructive", title: "Can't remove", description: "Only the recipient can remove their own items." });
      return;
    }
    if (!confirm("Remove this item?")) return;
    const { error } = await deleteWishItem(item.id);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not remove item." });
    } else {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
              <div className="w-8" />
              <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                Registry
              </h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {recipientWishlists.length === 0 ? (
                <div className="text-center py-12">
                  <Gift size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {recipientName || "The recipient"} hasn't created a wishlist yet.
                  </p>
                </div>
              ) : (
                <>
                  {/* List tabs */}
                  {recipientWishlists.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {recipientWishlists.map((w) => (
                        <button
                          key={w.id}
                          onClick={() => setActiveListId(w.id)}
                          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            activeListId === w.id
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          {w.title}
                        </button>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Items added by {recipientName || "the recipient"} can't be removed. You can add suggestions of your own.
                  </p>

                  {/* Items grid */}
                  {loading ? (
                    <div className="text-center py-8 text-sm text-muted-foreground">Loading…</div>
                  ) : items.length === 0 ? (
                    <div className="text-center py-8 text-sm text-muted-foreground">No items yet</div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2.5">
                      {items.map((item) => {
                        const isMine = item.added_by_user_id === user?.id;
                        return (
                          <div
                            key={item.id}
                            className="group rounded-2xl bg-card overflow-hidden relative"
                          >
                            {isMine && (
                              <button
                                onClick={() => handleDelete(item)}
                                className="absolute top-1.5 end-1.5 z-10 p-1.5 rounded-full bg-background/80 backdrop-blur-sm text-muted-foreground hover:text-destructive transition-all"
                                aria-label="Remove"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                            {item.is_most_desired && (
                              <div className="absolute top-1.5 start-1.5 z-10 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                                <Star size={10} fill="currentColor" />
                              </div>
                            )}
                            <div className="relative w-full aspect-square bg-muted/30 overflow-hidden flex items-center justify-center p-2">
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <Gift size={20} className="text-muted-foreground/30" />
                              )}
                            </div>
                            <div className="px-2 pt-1.5 pb-2 space-y-0.5">
                              <p className="text-xs font-semibold text-foreground line-clamp-2 leading-snug">
                                {item.title}
                              </p>
                              {item.price != null && (
                                <p className="text-[11px] font-bold text-foreground">{item.price} AED</p>
                              )}
                              <div className="flex items-center justify-between gap-1">
                                {!isMine && (
                                  <span className="text-[9px] text-muted-foreground/70 truncate">
                                    by {recipientName || "recipient"}
                                  </span>
                                )}
                                {item.link_url && (
                                  <a
                                    href={item.link_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ms-auto inline-flex items-center gap-0.5 text-[9px] text-primary/80 hover:text-primary font-medium"
                                  >
                                    <ExternalLink size={8} /> View
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <button
                    onClick={() => setAddOpen(true)}
                    disabled={!activeListId}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground py-3 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Plus size={16} />
                    Add a suggestion
                  </button>
                </>
              )}
            </div>
          </motion.div>

          {activeListId && (
            <AddWishModal
              open={addOpen}
              onOpenChange={setAddOpen}
              wishlistId={activeListId}
              onAdded={() => {
                setAddOpen(false);
                loadItems(activeListId);
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OrganizerRegistryModal;
